import React, { ElementType, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { FiImage, FiUploadCloud } from 'react-icons/fi';
import { CatalogueItemImage } from '../../../utils/types';
import {
  CatalogueSectionCard,
  catalogueAccent,
  catalogueBorder,
  catalogueSurfaceMuted,
  formatDateLabel,
} from './catalogueUi';

type Props = {
  images: CatalogueItemImage[];
  onUpload?: (file: File) => Promise<void> | void;
  onDelete?: (imageId: string) => void;
  uploading?: boolean;
  title?: string;
  description?: string;
  allowUpload?: boolean;
};

const CatalogueMediaManager: React.FC<Props> = ({
  images,
  onUpload,
  onDelete,
  uploading = false,
  title = 'Media',
  description = 'Upload product images. The first image becomes the main cover in the catalogue.',
  allowUpload = true,
}) => {
  const fileRef = useRef<HTMLInputElement | null>(null);

  const primaryImage = useMemo(() => images[0], [images]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onUpload) {
      return;
    }

    onUpload(file);
    event.target.value = '';
  };

  return (
    <CatalogueSectionCard
      title={title}
      description={description}
      action={
        allowUpload ? (
          <Button
            onClick={() => fileRef.current?.click()}
            borderRadius="14px"
            leftIcon={<Box as={FiUploadCloud as ElementType} />}
            bg={catalogueSurfaceMuted}
            borderWidth="1px"
            borderColor={catalogueBorder}
            _hover={{ bg: 'gray.100' }}
            isLoading={uploading}
            loadingText="Uploading"
          >
            Add media
          </Button>
        ) : undefined
      }
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <Stack spacing={4}>
        <Box
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="22px"
          overflow="hidden"
          bg={catalogueSurfaceMuted}
          minH={{ base: '220px', md: '320px' }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          {primaryImage ? (
            <>
              <Image
                src={primaryImage.image}
                alt="Primary catalogue media"
                w="full"
                h="full"
                objectFit="cover"
              />
              <Box
                position="absolute"
                top={4}
                left={4}
                px={3}
                py={1.5}
                bg="rgba(17, 24, 39, 0.72)"
                color="white"
                borderRadius="full"
                fontSize="xs"
                fontWeight="700"
              >
                Cover image
              </Box>
            </>
          ) : (
            <VStack spacing={3} px={6} textAlign="center">
              <Box
                w="56px"
                h="56px"
                borderRadius="18px"
                bg="rgba(18, 128, 92, 0.10)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box as={FiImage as ElementType} boxSize={6} color={catalogueAccent} />
              </Box>
              <Text fontSize="md" fontWeight="700" color="gray.900">
                No product images yet
              </Text>
              <Text fontSize="sm" color="gray.500" maxW="sm">
                Add an image so this item is easier to recognize in your
                catalogue.
              </Text>
              {allowUpload ? (
                <Button
                  onClick={() => fileRef.current?.click()}
                  borderRadius="14px"
                  bg={catalogueAccent}
                  color="white"
                  _hover={{ bg: '#0f6a4d' }}
                >
                  Upload first image
                </Button>
              ) : null}
            </VStack>
          )}
        </Box>

        {uploading ? (
          <Box>
            <Progress size="xs" isIndeterminate borderRadius="full" />
            <Text fontSize="sm" color="gray.500" mt={2}>
              Uploading image...
            </Text>
          </Box>
        ) : null}

        {images.length > 0 ? (
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
            {images.map((image, index) => (
              <Box
                key={image.id}
                bg={catalogueSurfaceMuted}
                borderWidth="1px"
                borderColor="gray.100"
                borderRadius="18px"
                overflow="hidden"
              >
                <Box h={{ base: '128px', md: '160px' }}>
                  <Image
                    src={image.image}
                    alt={`Catalogue image ${index + 1}`}
                    w="full"
                    h="full"
                    objectFit="cover"
                  />
                </Box>
                <HStack justify="space-between" px={3} py={3} align="flex-start">
                  <Box>
                    <Text fontSize="sm" fontWeight="700" color="gray.900">
                      {index === 0 ? 'Primary image' : `Image ${index + 1}`}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {formatDateLabel(image.uploaded_at)}
                    </Text>
                  </Box>
                  {onDelete ? (
                    <IconButton
                      aria-label="Delete image"
                      icon={<DeleteIcon />}
                      variant="ghost"
                      colorScheme="red"
                      size="sm"
                      onClick={() => onDelete(image.id)}
                    />
                  ) : null}
                </HStack>
              </Box>
            ))}
          </SimpleGrid>
        ) : null}
      </Stack>
    </CatalogueSectionCard>
  );
};

export default CatalogueMediaManager;
